/**
 * External dependencies
 */
import React, { memo } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Popover from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Image from '@naruto/naruto-components/src/base/v1/Image';
import SettingsControl from '@naruto/bgdapp/src/components/Common/SettingControl';
import useLayout from '@naruto/naruto-components/src/sxLayout';
import useMaterialTheme from '@naruto/naruto-hooks/src/useMaterialTheme';

/**
 * Internal dependencies
 */
import AllWrite from '@naruto/bgdapp/src/components/Common/AllWrite';
import useLang from '@naruto/bgdapp/src/hooks/useLang';
import { loadImage } from '@naruto/bgdapp/src/tools/image-loader';
import SideItem from './SideItem';
import useData from './useData';

function Header(): React.ReactElement {
  /* Global & Local State */
  const { i18ns } = useLang();
  const { general, mergedSx } = useLayout();
  const theme = useMaterialTheme();
  const {
    menuOpen,
    langOptions,
    sizeOptions,
    currentLang,
    fontScale,
    mainRoutes,
    anchorEl,
    gotoHome,
    toggleMenu,
    handleScaleChange,
    handleLangChange,
    handleChangeRoute,
    handleClick,
    handleClose
  } = useData();

  /* Main */
  return (
    <Box
      sx={{
        ...mergedSx({
          height: 'auto', // 這裡建議由內容撐開，或者保持原本邏輯，但在 Menu 打開時不能鎖死 height
          position: 'relative', // 確保層級正確
          zIndex: 1200, // 確保選單在最上層
          bgcolor: theme.palette.background.default, // 確保背景色一致
          borderBottom: `1px solid ${theme.palette.grey[400]}`
        })
      }}
    >
      <Box sx={{ ...mergedSx(general.FLEX_SPACE_BETWEEN_ALIGN_CENTER, { height: '56px', px: 4 }) }}>
        <Box sx={{ width: '80px', height: 25, cursor: 'pointer' }} onClick={gotoHome}>
          <Image src={loadImage('logo.svg')} typeSize="fit" />
        </Box>
        <Box>
          <IconButton onClick={handleClick} sx={{ mr: 4 }}>
            <VisibilityIcon sx={{ fontSize: '20px', color: '#83A06D' }} />
          </IconButton>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            sx={{ borderRadius: '8px' }}
          >
            <Box
              sx={{
                maxWidth: '320px',
                minWidth: '286px',
                height: '48px',
                px: 2,
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              <SettingsControl
                sx={{
                  width: '100%'
                }}
                fullWidth
                variant="flat"
                sizeLabel={i18ns.__('frontend.global.property.fontsize')}
                languageOptions={langOptions}
                sizeOptions={sizeOptions}
                lang={currentLang}
                scale={fontScale}
                onLangChange={handleLangChange}
                onScaleChange={handleScaleChange}
              />
            </Box>
          </Popover>
          <ButtonBase onClick={toggleMenu}>
            <Typography
              variant="caption"
              color="textPrimary"
              sx={{
                fontWeight: 'normal',
                fontSize: { xs: '0.75rem', sm: '0.75rem', md: '0.875rem' }
              }}
            >
              {menuOpen
                ? i18ns.__('frontend.global.operation.menuclose')
                : i18ns.__('frontend.global.operation.menuopen')}
            </Typography>
          </ButtonBase>
        </Box>
      </Box>
      <Collapse in={menuOpen} timeout="auto" unmountOnExit>
        <Box
          sx={{
            height: '100vh',
            background: theme.palette.background.default,
            px: 4
          }}
        >
          <List component="nav" sx={{ pb: 4, pt: 20, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {mainRoutes.map(
              (route, index) =>
                route.showInSidebar && (
                  <SideItem key={index} item={route} parentRoute="" onChangeRoute={handleChangeRoute} />
                )
            )}
          </List>
          <Box
            sx={{
              width: '100%',
              height: '40px',
              position: 'fixed',
              bottom: 0,
              left: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <AllWrite />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export default memo(Header);
